import {
  AppstoreOutlined,
  CalculatorOutlined,
  KeyOutlined,
  LoginOutlined,
  RobotOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { Form, Input, Select } from 'antd';
import SalaryViewersCard from '@/components/compensation/SalaryViewersCard';
import { Group, SettingSwitch } from '@/components/profile/settingCards';
import { cn } from '@/libs/utils';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';
import CompanyAccessAssignments from './CompanyAccessAssignments';

/**
 * The "Other modules" and "People and access" tiles of the company profile as
 * setting cards. Same fields, visibility rules and save as
 * CompanyModulesAccessSettings and CompanyAccessAssignments, which step aside
 * on the profile.
 */
export default function CompanyModulesAccessProfilePanel() {
  const ctx = useCompanyGeneralDataForm();
  const {
    readOnly,
    sectionClass,
    ssoAllowedDomainsKeys,
    ssoAllowedDomainsState,
    ssoTenantIdKeys,
    ssoTenantIdState,
    t,
  } = ctx;
  const f = (key) => t(key, { ns: 'forms' });
  const lock = (state) => readOnly || !!state?.disabled;
  const visible = (name) => !!ctx[`${name}State`]?.visible;

  // `name` = the context prefix of <name>Keys / <name>State.
  const toggle = (name, props) =>
    visible(name) && (
      <Form.Item name={ctx[`${name}Keys`]} noStyle>
        <SettingSwitch locked={lock(ctx[`${name}State`])} {...props} />
      </Form.Item>
    );

  const ssoFields = (ssoTenantIdState?.visible ||
    ssoAllowedDomainsState?.visible) && (
    <div className="grid gap-2 pl-[42px]">
      {ssoTenantIdState?.visible && (
        <Form.Item
          name={ssoTenantIdKeys}
          label={f('ssoTenantId')}
          tooltip={f('ssoTenantIdHint')}
          className="!mb-0"
        >
          <Input
            size="small"
            placeholder={f('ssoTenantId')}
            disabled={lock(ssoTenantIdState)}
          />
        </Form.Item>
      )}
      {ssoAllowedDomainsState?.visible && (
        <Form.Item
          name={ssoAllowedDomainsKeys}
          label={f('ssoAllowedDomains')}
          tooltip={f('ssoAllowedDomainsHint')}
          className="!mb-0"
        >
          <Select
            size="small"
            mode="tags"
            open={false}
            tokenSeparators={[',', ' ']}
            placeholder={f('ssoAllowedDomains')}
            disabled={lock(ssoAllowedDomainsState)}
          />
        </Form.Item>
      )}
    </div>
  );

  const hasModules =
    visible('toolsEnabled') ||
    visible('calculatorEnabled') ||
    visible('assistantEnabled');
  const hasSecurity = visible('mfaEnabled') || visible('ssoOnly');

  return (
    <>
      {hasModules && (
        <div className={sectionClass('moduli')}>
          <Group title={f('moduleVisibility')}>
            {toggle('toolsEnabled', {
              icon: <ToolOutlined />,
              title: f('toolsModuleEnabled'),
            })}
            {toggle('calculatorEnabled', {
              icon: <CalculatorOutlined />,
              title: f('calculatorModuleEnabled'),
            })}
            {toggle('assistantEnabled', {
              icon: <RobotOutlined />,
              title: f('assistantModuleEnabled'),
            })}
            {toggle('activityFeedEnabled', {
              icon: <AppstoreOutlined />,
              title: f('activityFeedEnabled'),
            })}
          </Group>
        </div>
      )}

      <div className={cn('grid gap-4', sectionClass('pristup'))}>
        <CompanyAccessAssignments />
        <SalaryViewersCard />

        {hasSecurity && (
          <Group title={f('security')}>
            {toggle('mfaEnabled', {
              icon: <KeyOutlined />,
              title: f('mfaEnabledCompanyLabel'),
            })}
            {toggle('ssoOnly', {
              icon: <LoginOutlined />,
              title: f('ssoOnlyLabel'),
              desc: f('ssoOnlyHint'),
              wide: !!ssoFields,
              children: ssoFields,
            })}
          </Group>
        )}
        {/* SSO fields of a role that does not see the SSO switch. */}
        {!visible('ssoOnly') && ssoFields}
      </div>
    </>
  );
}
